import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/items-suppliers.js

/**
 * ItemsSuppliersHandler
 *
 * Manages items supplier data (CA dimension classifications) with caching:
 * - Loads supplier data with all 17 CA dimension fields from latest year
 * - Implements intelligent caching to avoid redundant API calls
 * - Chunked loading for large datasets (100 items/page initially, then background)
 * - Error resilience with graceful fallback
 *
 * Year discovery probes the API backwards through up to 15 years; callers
 * are expected to gate this on a CA-items entitlement (lead firms only)
 * so free-tier projects don't trigger that probe at all.
 */
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => { if (isDev) console.log(...args); };

export class ItemsSuppliersHandler {
  /**
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    this.isLoading = false;
    this.lastFetchTime = 0;
    this._loadingPromise = null;

    // Cache validity period (5 minutes)
    this.cacheMaxAge = 5 * 60 * 1000;

    // Discovered-year cache per project. Stores the resolved year, OR `null`
    // when the probe found no data — both states are cached so we never
    // re-walk the 15-year backwards loop for the same project in one session.
    // { projectId: number|null }
    this._yearCache = {};

    // In-flight discovery promises, deduped by projectId. Without this,
    // parallel callers (loadItemsSuppliers + useScmCaYear hook) each kick
    // off their own 15-year probe — visible in the logs as duplicated
    // "Testing year X" lines.
    this._discoveryInFlight = {};

    // Background loading progress tracking
    this.loadingProgress = {
      isLoading: false,
      loaded: 0,
      total: 0
    };
  }

  /**
   * Load items suppliers data (CA dimension classifications) with caching
   * @param {boolean} forceRefresh - Force refresh regardless of cache status
   * @returns {Promise<Array>} Array of items supplier objects with dimension fields
   */
  async loadItemsSuppliers(forceRefresh = false) {
    try {
      // If already loading, wait for current load to complete
      if (this.isLoading) {
        if (this._loadingPromise) {
          await this._loadingPromise;
        }
        const cached = this.store.state.data.itemsSuppliers?.list || [];
        return cached;
      }

      // Set loading state
      this.store.state.loading.itemsSuppliers = true;
      this.isLoading = true;

      // Store the loading promise so concurrent calls can wait on it
      this._loadingPromise = (async () => {
        try {
          // Check if we have cached data
          const hasFetched = this.lastFetchTime > 0;

          if (!forceRefresh && hasFetched) {
            const cacheAge = Date.now() - this.lastFetchTime;
            if (cacheAge < this.cacheMaxAge) {
              // Using cached data
              const cached = this.store.state.data.itemsSuppliers?.list || [];
              return cached;
            }
          }

          // Fetch fresh data
          const result = await this._fetchItemsSuppliersData();

          this.lastFetchTime = Date.now();

          return result;
        } finally {
          // Always reset loading state
          this.isLoading = false;
          this._loadingPromise = null;
          this.store.state.loading.itemsSuppliers = false;
        }
      })();

      return this._loadingPromise;
    } catch (error) {
      if (isDev) {
        console.error('[ItemsSuppliersHandler] Failed to load items suppliers:', error);
      }
      this.store.handleError('itemsSuppliers', error);

      // Reset loading state
      this.isLoading = false;
      this._loadingPromise = null;
      this.store.state.loading.itemsSuppliers = false;

      // Return existing data or empty array instead of throwing
      const fallback = this.store.state.data.itemsSuppliers?.list || [];
      return fallback;
    }
  }

  /**
   * Fetch items suppliers data from API with chunked loading
   * @private
   * @returns {Promise<Array>} Array of supplier objects
   */
  async _fetchItemsSuppliersData() {
    const projectId = this.store.getProjectId();
    if (!projectId) {
      if (isDev) {
        console.warn('[ItemsSuppliersHandler] No project ID available when loading items suppliers');
      }
      return [];
    }

    const startTime = performance.now();

    // Discover which year has data for this project
    const yearWithData = await this._discoverDataYear(projectId);

    if (!yearWithData) {
      // Project has no CA items data — bail quietly. Caller is expected to
      // gate on lead-firm privilege; if we got here on a non-lead-firm
      // project the real fix is at the call site, not here.
      return [];
    }

    // Get first chunk immediately (100 items) for the discovered year
    const firstChunk = await this._fetchChunk(projectId, 0, 100, yearWithData);

    const totalCount = firstChunk.meta?.total || firstChunk.data?.length || 0;

    // Initialize loading progress tracking
    this.loadingProgress = {
      isLoading: totalCount > 100,
      loaded: firstChunk.data?.length || 0,
      total: totalCount
    };

    // Update store with first chunk for immediate display
    this._updateStore(firstChunk.data || [], startTime);

    // Notify subscribers of initial progress
    this.store.notifySubscribers('itemsSuppliers:loadingProgress', { ...this.loadingProgress });

    // If more data exists, load in background
    if (totalCount > 100) {
      this._loadRemainingInBackground(projectId, 100, totalCount, yearWithData);
    }

    return firstChunk.data || [];
  }

  /**
   * Discover which year has data by testing backwards from current year.
   * Caches resolved year AND null result per project. Dedups concurrent
   * callers via a shared in-flight promise.
   * @private
   */
  async _discoverDataYear(projectId) {
    // Resolved year cache (positive result)
    if (this._yearCache[projectId]) {
      return this._yearCache[projectId];
    }

    // Negative result cache — `null` is set when a probe completes and
    // finds nothing, so the next call returns null without re-walking
    // 15 years. `in` distinguishes "cached null" from "never probed".
    if (projectId in this._yearCache) {
      return null;
    }

    // Dedup parallel callers
    if (this._discoveryInFlight[projectId]) {
      return this._discoveryInFlight[projectId];
    }

    const probe = this._probeYearsBackwards(projectId);
    this._discoveryInFlight[projectId] = probe;
    try {
      const result = await probe;
      this._yearCache[projectId] = result; // may be null — that's the negative cache
      return result;
    } finally {
      delete this._discoveryInFlight[projectId];
    }
  }

  /**
   * The actual 15-year backwards walk. Split out so _discoverDataYear can
   * wrap dedup + cache around it.
   * @private
   */
  async _probeYearsBackwards(projectId) {
    devLog('[ItemsSuppliersHandler] Discovering CA data year for project', projectId);

    const startYear = new Date().getFullYear();
    const maxYearsBack = 15;

    for (let i = 0; i <= maxYearsBack; i++) {
      const testYear = startYear - i;

      try {
        const testUrl = `${process.env.GATSBY_STRAPI_URL}/api/ca-items-supplier/project/${projectId}/year/${testYear}?page=1&limit=1`;
        const response = await fetch(testUrl, {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`,
            'Cache-Control': 'no-cache'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const hasData = (data.data?.length || 0) > 0 || (data.meta?.total || 0) > 0;

          if (hasData) {
            devLog(`[ItemsSuppliersHandler] Found CA data for year ${testYear}`);
            return testYear;
          }
          // 200 + empty: keep walking back
          continue;
        }

        if (response.status === 404 || response.status === 400) {
          // Year doesn't exist for this project — keep walking
          continue;
        }

        if (isDev) {
          console.warn(`[ItemsSuppliersHandler] Unexpected status ${response.status} for year ${testYear}`);
        }
      } catch (error) {
        if (isDev) {
          console.warn(`[ItemsSuppliersHandler] Error probing year ${testYear}:`, error.message);
        }
      }
    }

    if (isDev) {
      console.info(`[ItemsSuppliersHandler] No CA data found for project ${projectId} in last ${maxYearsBack} years`);
    }
    return null;
  }

  /**
   * Get the cached discovered year for a project (instant, no API call)
   * @param {number} projectId
   * @returns {number|null} The discovered year, or null if not yet discovered
   */
  getDiscoveredYear(projectId) {
    return this._yearCache[projectId] || null;
  }

  /**
   * Discover the latest CA year with data (public wrapper around _discoverDataYear)
   * Returns cached result if available, otherwise probes the API.
   * @param {number} projectId
   * @returns {Promise<number|null>}
   */
  async discoverYear(projectId) {
    return this._discoverDataYear(projectId);
  }

  /**
   * Fetch a single chunk of items supplier data
   * @private
   * @param {number} projectId - Project ID
   * @param {number} offset - Pagination offset
   * @param {number} limit - Page size limit
   * @param {number} yearToQuery - The year to fetch data for (from year discovery)
   * @param {number} retryCount - Current retry attempt count
   */
  async _fetchChunk(projectId, offset, limit, yearToQuery, retryCount = 0) {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_BASE = 1000;
    const FETCH_TIMEOUT = 15000;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

      const url = `${process.env.GATSBY_STRAPI_URL}/api/ca-items-supplier/project/${projectId}/year/${yearToQuery}?page=${Math.floor(offset / limit) + 1}&limit=${limit}`;

      const response = await fetch(
        url,
        {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`,
            'Cache-Control': 'no-cache'
          },
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (isDev) {
          console.warn('[ItemsSuppliersHandler] API response not ok', { status: response.status, url, yearQueried: yearToQuery });
        }
        throw new Error(`Failed to fetch chunk: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // Retry on network errors or timeouts
      if (retryCount < MAX_RETRIES) {
        const retryDelay = RETRY_DELAY_BASE * (retryCount + 1);
        if (isDev) {
          console.warn(`[ItemsSuppliersHandler] Chunk fetch failed at offset ${offset}, retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        }
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this._fetchChunk(projectId, offset, limit, yearToQuery, retryCount + 1);
      }

      if (isDev) {
        console.error(`[ItemsSuppliersHandler] Max retries exceeded for offset ${offset}:`, error);
      }
      return { data: [], meta: { total: 0, hasMore: false } };
    }
  }

  /**
   * Load remaining data in background
   * @private
   * @param {number} projectId - Project ID
   * @param {number} startOffset - Starting offset (after first chunk)
   * @param {number} totalCount - Total count of items to load
   * @param {number} yearWithData - The year that has data (from year discovery)
   */
  async _loadRemainingInBackground(projectId, startOffset, totalCount, yearWithData) {
    let offset = startOffset;
    const chunkSize = 1000;

    while (offset < totalCount) {
      const chunk = await this._fetchChunk(projectId, offset, chunkSize, yearWithData);

      if (chunk.data && chunk.data.length > 0) {
        // Append to existing data
        const currentList = this.store.state.data.itemsSuppliers?.list || [];
        const newList = [...currentList, ...chunk.data];

        // Update store silently (no saveState) for background chunks
        this._updateStore(newList, performance.now(), null, true);

        // Update loading progress
        this.loadingProgress = {
          isLoading: true,
          loaded: newList.length,
          total: totalCount
        };
        this.store.notifySubscribers('itemsSuppliers:loadingProgress', { ...this.loadingProgress });
      }

      offset += chunkSize;

      // Delay between chunks to avoid overwhelming backend
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Mark loading as complete
    const finalCount = this.store.state.data.itemsSuppliers?.list?.length || 0;
    this.loadingProgress = {
      isLoading: false,
      loaded: finalCount,
      total: totalCount
    };
    this.store.notifySubscribers('itemsSuppliers:loadingProgress', { ...this.loadingProgress });
  }

  /**
   * Update the store with items suppliers data
   * @private
   */
  _updateStore(data, startTime, error = null, silent = false) {
    // Ensure data is an array
    if (!Array.isArray(data)) {
      data = [];
    }

    // Initialize the data structure if needed
    if (!this.store.state.data.itemsSuppliers) {
      this.store.state.data.itemsSuppliers = {
        list: [],
        isReady: false,
        loadedAt: null
      };
    }

    // Update the store state
    this.store.state.data.itemsSuppliers.list = data;
    this.store.state.data.itemsSuppliers.loadedAt = new Date().toISOString();

    // Mark as ready once we have any data
    if (data.length > 0 || !silent) {
      this.store.state.data.itemsSuppliers.isReady = true;
    }

    // Notify subscribers
    if (!silent) {
      this.store.notifySubscribers('itemsSuppliers:updated', {
        count: data.length,
        loadTime: performance.now() - startTime,
        error
      });
    }
  }

  /**
   * Clear the cached items suppliers data
   */
  clearCache() {
    this.lastFetchTime = 0;
    this._yearCache = {};
    this._discoveryInFlight = {};
    this.loadingProgress = { isLoading: false, loaded: 0, total: 0 };
    if (this.store.state.data.itemsSuppliers) {
      this.store.state.data.itemsSuppliers.list = [];
      this.store.state.data.itemsSuppliers.isReady = false;
    }
  }
}
